import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { Box, Typography, Switch, TextField, IconButton } from "@mui/material";
import { Cancel, AddCircleTwoTone } from "@material-ui/icons";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  parentBoxStyle,
  infoStyle,
  childBoxStyleSpaceBetween,
  employee_use,
  themes,
  tableHeaderSx,
  tableRowSx,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import {
  specialCharacterRestrict,
  onlyNumberRegex,
  aadharRegex,
  panRegex,
  mustbe12Digits,
  pfNumberRegex,
  ifscCodePatterns,
  atleastOneCharacterWithSpace,
} from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { useFetch } from "services/hooks/useFetch";

export const EmployeePersonalInfo = ({
  handleNext,
  onUpdate,
  data,
  reCallApi,
  onClose,
  basicinfo,
}) => {
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...data,
      dob: null,
      transport_type: "Company accommodation",
      rows: [{ contact_name: "", relation_type: "", contact_number: "" }],
    },
  });

  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { userid } = useSelector((state) => state.hrportal);
  const [aadharNo, setAadharNo] = useState("");
  const [panNo, setPanNo] = useState("");
  const [pfNo, setPFNo] = useState("");
  const [esiNo, setESINo] = useState("");
  const [uanNo, setUANNo] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [addressDetails, setAddressDetails] = useState("");
  const [bankNameDetails, setBankNameDetails] = useState("");
  const [accountNameDetails, setAccountNameDetails] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCodeDetails, setIFSCcodeDetails] = useState("");
  const [bankAddress, setBankAddress] = useState("");
  const [saveAndExit, setSaveAndExit] = useState(false);
  const { result: gender_type } = useFetch(`${domain}get-gender-choices/`);
  const { result: maritial_status } = useFetch(`${domain}get-marital-choices/`);
  const [permanentAddressDetails, setPermanentAddressDetails] = useState("");
  const [employeeUse, setEmployeeUse] = useState("Company accommodation");
  const [maritialSts, setMaritialSts] = useState("");
  const [genderType, setGenderType] = useState("");
  const [blockNo, setBlockNo] = useState("");
  const [roomNo, setRoomNo] = useState("");
  const [communicationAddressCheckBox, setCommunicationAddressCheckBox] =
    useState(false);
  const [rows, setRows] = useState([
    { contact_name: "", relation_type: "", contact_number: "" },
  ]);
  const [rowError, setRowError] = useState([
    { contact_name: false, relation_type: false, contact_number: false },
  ]);

  const handleAddRow = () => {
    const isValid = true;
    if (!isValid) return;

    setRows((prev) => [
      ...prev,
      { contact_name: "", relation_type: "", contact_number: "" },
    ]);
    setRowError((prev) => [
      ...prev,
      { contact_name: false, relation_type: false, contact_number: false },
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowError((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);

    const updatedErrors = [...rowError];
    if (!updatedErrors[index])
      updatedErrors[index] = {
        contact_name: false,
        relation_type: false,
        contact_number: false,
      };
    updatedErrors[index][field] = false;
    setRowError(updatedErrors);
  };

  const onSubmit = async (data) => {
    const isValid = true;
    const contactData = rows.map((row) => ({
      contact_name: row.contact_name,
      relation_type: row.relation_type,
      contact_number: row.contact_number,
    }));

    const payload = {
      ...data,
      emergency_contacts: contactData,
      user_id: userid,
      phone_number: basicinfo.phone_number,
    };

    if (isValid) {
      setValue("button_loader", true);
      onUpdate({
        ...data,
        rows,
        user_id: userid,
      });
      const response = await customAPI(
        `${domain}employee-onboard`,
        "PUT",
        payload,
      );
      if (response.ok) {
        if (saveAndExit) {
          showToast("success", "Employee details updated successfully");
          setValue("button_loader", false);
          onClose();
          reCallApi(true);
          reset();
        } else {
          handleNext(2);
          reset();
        }
      } else {
        setSaveAndExit(false);
      }
    }
  };

  const updateAadharCode = (event) => {
    if (
      onlyNumberRegex.test(event.target.value) &&
      event.target.value.length <= 12
    ) {
      setValue("aadhar", event.target.value);
      setAadharNo(event.target.value);
    }
  };

  const updatePanNumber = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 10
    ) {
      setValue("pan", event.target.value);
      setPanNo(event.target.value);
    }
  };

  const updatePFNumber = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 22
    ) {
      setValue("pf", event.target.value);
      setPFNo(event.target.value);
    }
  };

  const handleDateOfBirth = (event) => {
    setValue("dob", event.target.value);
    setDateOfBirth(event.target.value);
  };

  const updateAddress = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("address", event.target.value);
      setAddressDetails(event.target.value);
      if (communicationAddressCheckBox) {
        setValue("permanent_address", event.target.value);
        setPermanentAddressDetails(event.target.value);
      }
    }
  };

  const updateBankAddress = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("bank_address", event.target.value);
      setBankAddress(event.target.value);
    }
  };

  const updateUANNumber = (event) => {
    if (
      onlyNumberRegex.test(event.target.value) &&
      event.target.value.length <= 12
    ) {
      setUANNo(event.target.value);
      setValue("uan", event.target.value);
    }
  };

  const updateESINumber = (event) => {
    if (
      onlyNumberRegex.test(event.target.value) &&
      event.target.value.length <= 17
    ) {
      setValue("esi", event.target.value);
      setESINo(event.target.value);
    }
  };

  const handleEmployeeUse = (event, data) => {
    if (data) {
      if (getValues("transport_type") !== data.label) {
        setBlockNo("");
        setRoomNo("");
        setValue("block_name", "");
        setValue("room_no", "");
        setValue("transport_route_no", "");
        setValue("transport_type", data.label);
        setEmployeeUse(data.label);
      }
    }
  };

  const handleMaritialStatus = (event, data) => {
    if (data) {
      if (getValues("marital_status") !== data.label) {
        setValue("marital_status", data.label);
        setMaritialSts(data.label);
      }
    }
  };

  const handleGenderType = (event, data) => {
    if (data) {
      if (getValues("gender") !== data.label) {
        setValue("gender", data.label);
        setGenderType(data.label);
      }
    }
  };

  const updateBlockNumber = (event) => {
    if (event.target.value.length < 32) {
      setValue("block_name", event.target.value);
      setBlockNo(event.target.value);
    }
  };

  const updateRoomNumber = (event) => {
    if (event.target.value.length <= 10) {
      setValue("room_no", event.target.value);
      setRoomNo(event.target.value);
    }
  };

  const updatePermanentAddress = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("permanent_address", event.target.value);
      setPermanentAddressDetails(event.target.value);
    }
  };

  const updateBankName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 64
    ) {
      setValue("bankname", event.target.value);
      setBankNameDetails(event.target.value);
    }
  };

  const updateAccountName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 64
    ) {
      setValue("account_holder_name", event.target.value);
      setAccountNameDetails(event.target.value);
    }
  };

  const updateBankAccountNumber = (event) => {
    if (
      event.target.value.length <= 17 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setValue("accountno", event.target.value);
      setAccountNumber(event.target.value);
    }
  };

  const updateIFSCcode = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 11
    ) {
      setValue("ifsc", event.target.value);
      setIFSCcodeDetails(event.target.value);
    }
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset(data);
      const mappedRows = data?.rows?.map((item) => ({
        contact_name: item.contact_name,
        relation_type: item.relation_type,
        contact_number: item.contact_number,
      }));
      setRows(mappedRows);
    }
  }, [data, reset]);

  return (
    <Box sx={parentBoxStyle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Identity & Statutory
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Date of Birth</Typography>
              <Typography {...infoStyle}>
                Employee's date of birth for age.
              </Typography>
              <Mui.TextField
                type="date"
                value={dateOfBirth}
                Icons={null}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
                rules={{
                  ...register("dob", {
                    required: false,
                  }),
                }}
                size="small"
                onChange={(event) => {
                  handleDateOfBirth(event);
                }}
                helperText={errors?.dob?.message ? errors?.dob?.message : ""}
                error={errors.dob ? true : false}
                fullWidth
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Aadhar Number</Typography>
              <Typography {...infoStyle}>
                Employee's 12-digit Aadhar number.
              </Typography>
              <CustomTextBox
                selectedValue={aadharNo}
                Icons={null}
                rules={{
                  ...register("aadhar", {
                    required: false,
                    pattern: {
                      pattern1: (value) =>
                        aadharRegex.test(value) ||
                        "Enter aadhar in format 123456789012",
                      pattern2: (value) =>
                        mustbe12Digits.test(value) ||
                        "Must be exactly 12 digits",
                    },
                    maxLength: {
                      value: 12,
                      message: "Must be exactly 12 digits",
                    },
                    minLength: {
                      value: 12,
                      message: "Must be exactly 12 digits",
                    },
                  }),
                }}
                updateDataFunction={updateAadharCode}
                placeHolder="Enter the aadhar number"
                errorMessage={
                  errors?.aadhar?.message ? errors?.aadhar?.message : ""
                }
                textBoxError={errors.aadhar ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>PAN Number</Typography>
              <Typography {...infoStyle}>
                Employee's PAN identification number.
              </Typography>
              <CustomTextBox
                selectedValue={panNo}
                Icons={null}
                rules={{
                  ...register("pan", {
                    required: false,
                    pattern: {
                      value: panRegex,
                      message: "Enter PAN in format AAAAA9999A",
                    },
                    minLength: {
                      value: 10,
                      message: "Must be exactly 10 characters",
                    },
                    validate: {
                      employeePANAvailable: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}unique/identity-numbers/?pan=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "PAN number already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updatePanNumber}
                placeHolder="Enter the PAN number"
                errorMessage={errors?.pan?.message ? errors?.pan?.message : ""}
                textBoxError={errors.pan ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>ESI Number</Typography>
              <Typography {...infoStyle}>
                Employee's ESI identification number.
              </Typography>
              <CustomTextBox
                selectedValue={esiNo}
                Icons={null}
                rules={{
                  ...register("esi", {
                    required: false,
                    minLength: {
                      value: 17,
                      message: "Must be exactly 17 digits",
                    },
                    validate: {
                      employeeESIAvailable: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}unique/identity-numbers/?esi=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "ESI number already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateESINumber}
                placeHolder="Enter the ESI number"
                errorMessage={errors?.esi?.message ? errors?.esi?.message : ""}
                textBoxError={errors.esi ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>PF Number</Typography>
              <Typography {...infoStyle}>
                Employee's PF identification number.
              </Typography>
              <CustomTextBox
                selectedValue={pfNo}
                Icons={null}
                rules={{
                  ...register("pf", {
                    required: false,
                    pattern: {
                      value: pfNumberRegex,
                      message: "Enter PF in format MHBAN00000160000000134",
                    },
                    minLength: {
                      value: 22,
                      message: "Must be exactly 22 characters",
                    },
                    validate: {
                      employeePFAvailable: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}unique/identity-numbers/?pf=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "PF number Already Exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updatePFNumber}
                placeHolder="Enter the PF number"
                errorMessage={errors?.pf?.message ? errors?.pf?.message : ""}
                textBoxError={errors.pf ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>UAN Number</Typography>
              <Typography {...infoStyle}>
                Employee's UAN identification number.
              </Typography>
              <CustomTextBox
                selectedValue={uanNo}
                Icons={null}
                rules={{
                  ...register("uan", {
                    required: false,
                    pattern: {
                      value: mustbe12Digits,
                      message: "Enter UAN in format 123456789012",
                    },
                    minLength: {
                      value: 12,
                      message: "Must be exactly 12 digits",
                    },
                    validate: {
                      employeeUANAvailable: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}unique/identity-numbers/?uan=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "UAN number already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateUANNumber}
                placeHolder="Enter the UAN number"
                errorMessage={errors?.uan?.message ? errors?.uan?.message : ""}
                textBoxError={errors.uan ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Address Info
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Gender</Typography>
              <Typography {...infoStyle}>
                Employee's gender as per official records.
              </Typography>
              <CustomAutoComplete
                dataList={gender_type}
                selectedValue={genderType}
                rules={{
                  ...register("gender", {
                    required: false,
                  }),
                }}
                updateDataFunction={handleGenderType}
                placeHolder="Select the gender type"
                errorMessage={
                  errors?.gender?.message ? errors?.gender?.message : ""
                }
                textBoxError={errors?.gender ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Marital Status</Typography>
              <Typography {...infoStyle}>
                Employee's current martial status.
              </Typography>
              <CustomAutoComplete
                dataList={maritial_status}
                selectedValue={maritialSts}
                rules={{
                  ...register("marital_status", {
                    required: false,
                  }),
                }}
                updateDataFunction={handleMaritialStatus}
                placeHolder="Select the maritial status"
                errorMessage={
                  errors?.marital_status?.message
                    ? errors?.marital_status?.message
                    : ""
                }
                textBoxError={errors?.marital_status ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={12}>
              <Typography sx={{ fontWeight: 600 }}>
                Communication Address
              </Typography>
              <Typography {...infoStyle}>
                Primary address used for correspondence and communication.
              </Typography>
              <CustomTextBox
                type="multiline"
                selectedValue={addressDetails}
                Icons={null}
                rules={{
                  ...register("address", {
                    required: false,
                  }),
                }}
                updateDataFunction={updateAddress}
                placeHolder="Enter Address"
                errorMessage={
                  errors?.address?.message ? errors?.address?.message : ""
                }
                textBoxError={errors.address ? true : false}
              />
            </Mui.Grid>
            <Box sx={childBoxStyleSpaceBetween}>
              <Switch
                checked={communicationAddressCheckBox}
                onChange={(event) => {
                  setCommunicationAddressCheckBox(event.target.checked);
                  if (event.target.checked) {
                    setValue("permanent_address", addressDetails);
                    setPermanentAddressDetails(addressDetails);
                  } else {
                    setValue("permanent_address", "");
                    setPermanentAddressDetails("");
                  }
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: 600 }}>
                  Is communication address same as permanent address
                </Typography>
              </Box>
            </Box>
            <Mui.Grid item xs={12}>
              <Typography sx={{ fontWeight: 600 }}>
                Permanent Address
              </Typography>
              <Typography {...infoStyle}>
                Employee's long-term residential address.
              </Typography>
              <CustomTextBox
                type="multiline"
                selectedValue={permanentAddressDetails}
                rules={{
                  ...register("permanent_address", {
                    required: false,
                  }),
                }}
                updateDataFunction={updatePermanentAddress}
                placeHolder="Enter Permanent Address"
                errorMessage={
                  errors?.permanent_address?.message
                    ? errors?.permanent_address?.message
                    : ""
                }
                textBoxError={errors.permanent_address ? true : false}
                disabledValue={
                  permanentAddressDetails === addressDetails ? true : false
                }
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Bank Info
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>
                Account Holder Name
              </Typography>
              <Typography {...infoStyle}>
                Name registered in the bank account.
              </Typography>
              <CustomTextBox
                selectedValue={accountNameDetails}
                rules={{
                  ...register("account_holder_name", {
                    required: false,
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateAccountName}
                placeHolder="Enter Account Name"
                errorMessage={
                  errors?.account_holder_name?.message
                    ? errors?.account_holder_name?.message
                    : ""
                }
                textBoxError={errors.account_holder_name ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Account Number</Typography>
              <Typography {...infoStyle}>
                Active bank account number.
              </Typography>
              <CustomTextBox
                selectedValue={accountNumber}
                rules={{
                  ...register("accountno", {
                    required: false,
                  }),
                }}
                updateDataFunction={updateBankAccountNumber}
                placeHolder="Enter Bank Account Number"
                errorMessage={
                  errors?.accountno?.message ? errors?.accountno?.message : ""
                }
                textBoxError={errors.accountno ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Bank Name</Typography>
              <Typography {...infoStyle}>Name of the bank.</Typography>
              <CustomTextBox
                selectedValue={bankNameDetails}
                rules={{
                  ...register("bankname", {
                    required: false,
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateBankName}
                placeHolder="Enter Bank Name"
                errorMessage={
                  errors?.bankname?.message ? errors?.bankname?.message : ""
                }
                textBoxError={errors.bankname ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Address</Typography>
              <Typography {...infoStyle}>
                Address of the bank branch.
              </Typography>
              <CustomTextBox
                selectedValue={bankAddress}
                Icons={null}
                rules={{
                  ...register("bank_address", {
                    required: false,
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateBankAddress}
                placeHolder="Enter Address"
                errorMessage={
                  errors?.bank_address?.message
                    ? errors?.bank_address?.message
                    : ""
                }
                textBoxError={errors.bank_address ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>IFSC Code</Typography>
              <Typography {...infoStyle}>
                Unique code for the bank branch.
              </Typography>
              <CustomTextBox
                selectedValue={ifscCodeDetails}
                rules={{
                  ...register("ifsc", {
                    required: false,
                    validate: {
                      minLengthNoSpaces: (value) => {
                        const trimmedValue = value?.trim();
                        if (!trimmedValue) {
                          return true;
                        }
                        if (trimmedValue.length < 11) {
                          return "Minimum 11 characters required";
                        }
                        if (!ifscCodePatterns.test(trimmedValue)) {
                          return "Enter a valid format: ABCD0123456";
                        }

                        return true;
                      },
                    },
                  }),
                }}
                updateDataFunction={updateIFSCcode}
                placeHolder="Enter IFSC Code"
                errorMessage={
                  errors?.ifsc?.message ? errors?.ifsc?.message : ""
                }
                textBoxError={errors.ifsc ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Emergency Contact
          </Mui.Typography>
          <Mui.Box>
            <Mui.TableContainer
              sx={{
                width: "100%",
              }}
            >
              <Mui.Table className="border">
                <Mui.TableHead sx={tableHeaderSx}>
                  <Mui.TableRow maxWidth="xl" align="left" sx={tableRowSx}>
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                      }}
                    >
                      Contact Name
                    </Mui.TableCell>{" "}
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                      }}
                    >
                      Relation Type
                    </Mui.TableCell>{" "}
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                      }}
                    >
                      Contact Number
                    </Mui.TableCell>{" "}
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                      }}
                    ></Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                {rows.map((row, index) => (
                  <Mui.TableBody>
                    <Mui.TableCell>
                      <TextField
                        size="small"
                        value={row.contact_name}
                        onChange={(e) => {
                          const val = e.target.value;

                          const validPattern = /^[A-Za-z ]*$/;

                          if (val === "" || validPattern.test(val)) {
                            handleChange(index, "contact_name", val);
                          }
                        }}
                        error={rowError[index]?.contact_name}
                        helperText={
                          rowError[index]?.contact_name
                            ? row.contact_name.trim() === ""
                              ? "Enter contact name"
                              : "Name must contain only letters"
                            : ""
                        }
                        placeholder="Enter name"
                      />
                    </Mui.TableCell>

                    <Mui.TableCell>
                      <TextField
                        size="small"
                        value={row.relation_type}
                        onChange={(e) => {
                          const val = e.target.value;

                          const validPattern = /^[A-Za-z ]*$/;

                          if (val === "" || validPattern.test(val)) {
                            handleChange(index, "relation_type", val);
                          }
                        }}
                        error={rowError[index]?.relation_type}
                        helperText={
                          rowError[index]?.relation_type
                            ? row.relation_type.trim() === ""
                              ? "Enter relation"
                              : index > 0 &&
                                  row.relation_type.trim().toLowerCase() ===
                                    rows[index - 1].relation_type
                                      .trim()
                                      .toLowerCase()
                                ? `Relation must be unique — previous: ${
                                    rows[index - 1].relation_type
                                  }`
                                : "Invalid relation type"
                            : ""
                        }
                        placeholder="Enter relation"
                      />
                    </Mui.TableCell>

                    <Mui.TableCell>
                      <TextField
                        size="small"
                        value={row.contact_number}
                        onChange={(e) => {
                          const val = e.target.value;
                          const validPattern = /^[0-9]{0,10}$/;

                          if (validPattern.test(val)) {
                            handleChange(index, "contact_number", val);
                          }
                        }}
                        error={rowError[index]?.contact_number}
                        helperText={
                          rowError[index]?.contact_number
                            ? row.contact_number.trim() === ""
                              ? "Enter contact number"
                              : row.contact_number.trim().length !== 10
                                ? "Number must be 10 digits"
                                : index > 0 &&
                                    row.contact_number ===
                                      rows[index - 1].contact_number
                                  ? `Number must be unique — previous: ${
                                      rows[index - 1].contact_number
                                    }`
                                  : "Invalid number"
                            : ""
                        }
                        placeholder="Enter number"
                      />
                    </Mui.TableCell>

                    <Mui.TableCell>
                      <IconButton
                        onClick={() => handleRemoveRow(index)}
                        disabled={rows?.length === 1}
                      >
                        <Cancel />
                      </IconButton>
                    </Mui.TableCell>
                  </Mui.TableBody>
                ))}
              </Mui.Table>
            </Mui.TableContainer>
            <Mui.Grid container>
              <Mui.Grid item xs={10.75}></Mui.Grid>
              <Mui.Grid item xs={1}>
                <IconButton onClick={handleAddRow}>
                  <AddCircleTwoTone />
                </IconButton>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Box>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Accommodation & Transport
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={12}>
              <Typography sx={{ fontWeight: 600 }}>Employee Use</Typography>
              <Typography {...infoStyle}>
                Select the type of accommodation or transport facility assigned
                to the employee.
              </Typography>
              <CustomAutoComplete
                dataList={employee_use}
                selectedValue={employeeUse}
                rules={{
                  ...register("transport_type", {
                    required: false,
                  }),
                }}
                updateDataFunction={handleEmployeeUse}
                placeHolder="Select the employee use"
                errorMessage={
                  errors?.transport_type?.message
                    ? errors?.transport_type?.message
                    : ""
                }
                textBoxError={errors?.transport_type ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Block Name</Typography>
              <Typography {...infoStyle}>
                Name of the building / block.
              </Typography>
              <CustomTextBox
                selectedValue={blockNo}
                rules={{
                  ...register("block_name", {
                    required: false,
                  }),
                }}
                updateDataFunction={updateBlockNumber}
                placeHolder="Enter block name"
                errorMessage={
                  errors?.block_name?.message ? errors?.block_name?.message : ""
                }
                textBoxError={errors.block_name ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Room No</Typography>
              <Typography {...infoStyle}>
                Room number assigned to the employee.
              </Typography>
              <CustomTextBox
                selectedValue={roomNo}
                rules={{
                  ...register("room_no", {
                    required: false,
                  }),
                }}
                updateDataFunction={updateRoomNumber}
                placeHolder="Enter room number"
                errorMessage={
                  errors?.room_no?.message ? errors?.room_no?.message : ""
                }
                textBoxError={errors.room_no ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Grid container sx={{ padding: 2 }}>
          <Mui.Grid xs={3}>
            <CustomButton
              actionFuntion={() => {
                setSaveAndExit(true);
              }}
              actionName="Save & Exit"
              typeName="submit"
            />
          </Mui.Grid>
          <Mui.Grid xs={6}></Mui.Grid>
          <Mui.Grid
            xs={3}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <CustomButton
              actionFuntion={handleSubmit(onSubmit)}
              actionName="Next"
              typeName="submit"
              disableAction={watch("button_loader")}
            />
          </Mui.Grid>
        </Mui.Grid>

        <Mui.Grid
          container
          sx={{
            paddingBottom: { xs: 2, sm: 4, md: 4, lg: 5, xl: 5, xxl: 5 },
          }}
        />
      </form>
    </Box>
  );
};
